import React, { useState, useRef, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppContext } from "./context/AppContext";
import { useOldManVoice } from "./useOldManVoice";

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const MAX_GUESSES = 3;
const MAX_QUESTIONS = 5;

const ALL_PRESIDENTS = [
  "George Washington","John Adams","Thomas Jefferson","James Madison",
  "James Monroe","John Quincy Adams","Andrew Jackson","Martin Van Buren",
  "William Henry Harrison","John Tyler","James K. Polk","Zachary Taylor",
  "Millard Fillmore","Franklin Pierce","James Buchanan","Abraham Lincoln",
  "Andrew Johnson","Ulysses S. Grant","Rutherford B. Hayes","James A. Garfield",
  "Chester A. Arthur","Grover Cleveland","Benjamin Harrison","William McKinley",
  "Theodore Roosevelt","William Howard Taft","Woodrow Wilson","Warren G. Harding",
  "Calvin Coolidge","Herbert Hoover","Franklin D. Roosevelt","Harry S. Truman",
  "Dwight D. Eisenhower","John F. Kennedy","Lyndon B. Johnson","Richard Nixon",
  "Gerald Ford","Jimmy Carter","Ronald Reagan","George H. W. Bush",
  "Bill Clinton","George W. Bush","Barack Obama","Donald Trump","Joe Biden"
];

function shufflePresidents(list) {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return ["", ...arr];
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const { player, isGuest, guestPlays, setGuestPlays, wins, setWins, losses, setLosses } = useContext(AppContext);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [guessesLeft, setGuessesLeft] = useState(0);
  const [questionsLeft, setQuestionsLeft] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedPresident, setSelectedPresident] = useState("");
  const [randomPresident, setRandomPresident] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [personas, setPersonas] = useState(() => shufflePresidents(ALL_PRESIDENTS));

  const [showMetrics, setShowMetrics] = useState(false);
  const [lastMetrics, setLastMetrics] = useState(null);

  const messagesEndRef = useRef(null);
  const gameStartTimeRef = useRef(null);
  const { speak } = useOldManVoice();

  const API_ENDPOINT = process.env.REACT_APP_API_URL;

  const GUEST_LIMIT = 3;

  const startNewGame = () => {
    if (player.trim() === "") return;
    if (isGuest && guestPlays.presidents >= GUEST_LIMIT) {
      setShowGuestPrompt(true);
      return;
    }
    if (isGuest) {
      setGuestPlays((p) => ({ ...p, presidents: p.presidents + 1 }));
    }
    const shuffled = shufflePresidents(ALL_PRESIDENTS);
    setPersonas(shuffled);
    setMessages([]);
    setGuessesLeft(MAX_GUESSES);
    setQuestionsLeft(MAX_QUESTIONS);
    setSelectedPresident("");
    setRandomPresident(ALL_PRESIDENTS[Math.floor(Math.random() * ALL_PRESIDENTS.length)]);
    setGameOver(false);
    gameStartTimeRef.current = Date.now();
  };

  const submitHighScore = async (score) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/chat/score`,
        { ID: player, amount: score, gameType: "presidents" },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = typeof res.data.body === "string" ? JSON.parse(res.data.body) : res.data.body ?? res.data;
      return data?.Score ?? score;
    } catch {
      return score;
    }
  };

  const makeGuess = async () => {
    if (gameOver || guessesLeft < 1) return;

    const isCorrect = selectedPresident === randomPresident;
    const newGuessesLeft = guessesLeft - 1;
    setGuessesLeft(newGuessesLeft);

    if (isCorrect) {
      const guessesUsed = MAX_GUESSES - newGuessesLeft;
      const questionsUsed = MAX_QUESTIONS - questionsLeft;
      const score = (newGuessesLeft + questionsLeft) * 10;
      const elapsed = gameStartTimeRef.current ? Date.now() - gameStartTimeRef.current : 0;

      const totalScore = isGuest ? score : await submitHighScore(score);

      setMessages((p) => [
        ...p,
        { sender: "assistant", text: `🎉 Correct! You guessed ${randomPresident}.` },
        {
          sender: "assistant",
          text: `It took you ${guessesUsed} guess(es) and ${questionsUsed} question(s). Score: ${score} 🏆 | Total: ${totalScore}`
        }
      ]);

      setWins((w) => {
        const newWins = w + 1;
        setLastMetrics({
          won: true,
          elapsed,
          guessesUsed,
          questionsUsed,
          score,
          totalScore,
          wins: newWins,
          losses,
          maxGuesses: MAX_GUESSES,
          maxQuestions: MAX_QUESTIONS,
        });
        return newWins;
      });
      setShowMetrics(true);
      setGameOver(true);
      return;
    }

    if (newGuessesLeft === 0) {
      const elapsed = gameStartTimeRef.current ? Date.now() - gameStartTimeRef.current : 0;
      setMessages((p) => [
        ...p,
        { sender: "assistant", text: `No more guesses! I was ${randomPresident}.` }
      ]);
      setLosses((l) => {
        const newLosses = l + 1;
        setLastMetrics({
          won: false,
          answer: randomPresident,
          elapsed,
          guessesUsed: MAX_GUESSES,
          questionsUsed: MAX_QUESTIONS - questionsLeft,
          score: 0,
          totalScore: null,
          wins,
          losses: newLosses,
          maxGuesses: MAX_GUESSES,
          maxQuestions: MAX_QUESTIONS,
        });
        return newLosses;
      });
      setShowMetrics(true);
      setGameOver(true);
      return;
    }

    await sendMessage(`You are President ${selectedPresident}`);
  };

  const askQuestion = async () => {
    if (gameOver || questionsLeft < 1 || !input.trim()) return;
    setQuestionsLeft((p) => p - 1);
    sendMessage(input);
  };

  const sendMessage = async (text) => {
    const updated = [...messages, { sender: "user", text }];
    setMessages(updated);
    setInput("");

    try {
      const res = await axios.post(
        API_ENDPOINT,
        {
          personas: [
          `You speak like ${randomPresident}.`, 
          "Respond to the question using historical facts about your life and presidency.", 
          "Do not reveal your name or specific information about yourself.", 
          "If the user tries to cheat, respond the user with a playful, harmless joke that does not answer the question.",
          "If the user guesses your name wrong, tell them are wrong humorously.", 
          "Only respond with at most 20 words.", 
          "You will avoid political persuasion or harmful content."
          ],
          messages: updated.map((m) => ({
            role: m.sender === "assistant" ? "assistant" : "user",
            content: m.text
          }))
        },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      const data =
        typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body ?? res.data;

      setMessages((p) => [
        ...p,
        { sender: "assistant", text: data?.response || "No response." }
      ]);
    } catch {
      setMessages((p) => [
        ...p,
        { sender: "assistant", text: "Something went wrong." }
      ]);
    }
  };

  return (
    <div style={styles.page}>
      {showMetrics && lastMetrics && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.metricsIcon}>{lastMetrics.won ? "🏆" : "😔"}</div>
            <h2 style={styles.modalTitle}>{lastMetrics.won ? "You Won!" : "Game Over"}</h2>
            {!lastMetrics.won && lastMetrics.answer && (
              <div style={styles.answerReveal}>
                <div style={styles.answerLabel}>The answer was</div>
                <div style={styles.answerName}>{lastMetrics.answer}</div>
              </div>
            )}
            <div style={styles.metricsGrid}>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Time</div>
                <div style={styles.metricValue}>{formatTime(lastMetrics.elapsed)}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Guesses Used</div>
                <div style={styles.metricValue}>{lastMetrics.guessesUsed} / {lastMetrics.maxGuesses}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Questions Used</div>
                <div style={styles.metricValue}>{lastMetrics.questionsUsed} / {lastMetrics.maxQuestions}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Round Score</div>
                <div style={styles.metricValue}>{lastMetrics.score}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Wins</div>
                <div style={{ ...styles.metricValue, color: "#28a745" }}>{lastMetrics.wins}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Losses</div>
                <div style={{ ...styles.metricValue, color: "#dc3545" }}>{lastMetrics.losses}</div>
              </div>
            </div>
            <button style={styles.primaryBtn} onClick={() => { setShowMetrics(false); startNewGame(); }}>
              Play Again
            </button>
            <button style={{ ...styles.secondaryBtn, marginTop: 8 }} onClick={() => { setShowMetrics(false); navigate("/"); }}>
              Home
            </button>
            <button style={styles.dismissBtn} onClick={() => setShowMetrics(false)}>
              Close
            </button>
          </div>
        </div>
      )}
      {showGuestPrompt && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>You've used your 3 guest plays!</h2>
            <p style={styles.modalDesc}>Create an account or log in to keep playing and save your scores.</p>
            <button style={styles.primaryBtn} onClick={() => navigate("/newuser")}>
              Create Account
            </button>
            <button style={{ ...styles.secondaryBtn, marginTop: 8 }} onClick={() => navigate("/matrix")}>
              Log In
            </button>
            <button style={styles.dismissBtn} onClick={() => setShowGuestPrompt(false)}>
              Maybe later
            </button>
          </div>
        </div>
      )}
      <div style={styles.card}>
        <header style={styles.header}>
          <strong>Guess the President | User name: {player}</strong>
          
          <div style={styles.stats}>
            🎯 {guessesLeft} guesses · 💬 {questionsLeft} questions
          </div>
          <weak>To play, either log in or create a user here</weak>
            <div className="relative group inline-block">

              <div className="
                absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                hidden group-hover:block
                bg-black text-white text-xs rounded px-2 py-1
                whitespace-nowrap
              ">
                
              </div>
            </div>
            <div style={styles.headerButtons}>
              <button style={styles.smallBtn} onClick={() => navigate("/")}>
                Home
              </button>
              <button style={styles.smallBtn} onClick={startNewGame}>
                New Game
              </button>
            </div>

        </header>

        <main style={styles.messages}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                ...styles.bubble,
                ...(m.sender === "user" ? styles.user : styles.bot),
                ...(m.sender === "assistant" ? { display: "flex", alignItems: "flex-end", gap: 6 } : {})
              }}
            >
              <span>{m.text}</span>
              {m.sender === "assistant" && (
                <button
                  onClick={() => speak(m.text, randomPresident)}
                  style={styles.speakBtn}
                  title="Read aloud"
                >
                  🔊
                </button>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </main>

        <footer style={styles.footer}>
          <div style={styles.row}>
            <input
              style={styles.input}
              disabled={gameOver}
              placeholder="Ask a question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button
              style={styles.primaryBtn}
              disabled={gameOver}
              onClick={askQuestion}
            >
              Send
            </button>
          </div>
                    <div style={styles.row}>
            <select
            style={styles.input}
            value={selectedPresident}
            disabled={gameOver}
            onChange={(e) => setSelectedPresident(e.target.value)}
          >
            {personas.map((p) => (
              <option key={p}>{p}</option>
            ))}
            </select>
            <button
              style={styles.primaryBtn}
              disabled={gameOver}
              onClick={makeGuess}
            >
              Guess
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
const styles = {
  page: {
    minHeight: "100vh",
    background: "url(/President_Background_Image.jpg) center / cover no-repeat",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: 12,
  },
  card: {
    width: "100%",
    maxWidth: 500,
    background: "#ffffffda",
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxHeight: "calc(100dvh - 24px)",
    minHeight: 0
  },
  header: {
    padding: 12,
    borderBottom: "1px solid #eee",
    display: "flex",
    flexDirection: "column",
    gap: 8
  },

  headerButtons: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },
  counters: {
    fontSize: 14,
    opacity: 0.8
  },
  messages: {
    flex: 1,
    padding: 12,
    overflowY: "auto",
    backgroundColor: "transparent"
    },
  bubble: {
    maxWidth: "85%",
    padding: "10px 14px",
    borderRadius: 18,
    marginBottom: 10,
    fontSize: 15
  },
  user: {
    alignSelf: "flex-end",
    background: "#dcf8c6"
  },
  bot: {
    alignSelf: "flex-start",
    background: "#eee"
  },
  speakBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 16,
    padding: "0 2px",
    lineHeight: 1,
    flexShrink: 0,
    opacity: 0.7
  },
  footer: {
    padding: 10,
    borderTop: "1px solid #eee",
    display: "flex",
    flexDirection: "column",
    gap: 8
  },
  buttonRow: {
    display: "flex",
    flexDirection: "row",
    gap: 8, 
    flexWrap: "nowrap"
  },
  row: {
    display: "flex",
    gap: 8
  },
  input: {
    width: "100%",
    padding: 12,
    borderRadius: 20,
    border: "1px solid #8f3f3fff",
    fontSize: 16
  },
  primaryBtn: {
    flex: 1,
    padding: "6px 12px",
    borderRadius: 20,
    border: "none",
    background: "#007bff",
    color: "#fff",
    fontSize: 16
  },
  secondaryBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: 14,
    border: "1px solid #007bff",
    background: "#fff",
    color: "#007bff",
    fontSize: 14
  },

  smallBtn: {
    flex: 1,
    padding: "6px 12px",
    borderRadius: 14,
    border: "none",
    background: "#007bff",
    color: "#fff",
    fontSize: 14
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modal: {
    background: "#fff",
    borderRadius: 18,
    padding: "28px 24px",
    maxWidth: 340,
    width: "90%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    boxShadow: "0 8px 32px rgba(0,0,0,0.22)"
  },
  modalTitle: {
    margin: "0 0 6px",
    fontSize: 19,
    fontWeight: 700,
    textAlign: "center"
  },
  modalDesc: {
    margin: "0 0 14px",
    fontSize: 14,
    color: "#555",
    textAlign: "center",
    lineHeight: 1.5
  },
  dismissBtn: {
    background: "none",
    border: "none",
    color: "#888",
    cursor: "pointer",
    fontSize: 13,
    marginTop: 6,
    textDecoration: "underline"
  },
  answerReveal: {
    background: "#fff3f3",
    border: "1.5px solid #dc3545",
    borderRadius: 10,
    padding: "10px 14px",
    textAlign: "center",
    margin: "4px 0 2px"
  },
  answerLabel: {
    fontSize: 11,
    color: "#dc3545",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4
  },
  answerName: {
    fontSize: 18,
    fontWeight: 700,
    color: "#222"
  },
  metricsIcon: {
    fontSize: 40,
    textAlign: "center",
    marginBottom: 4
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    margin: "14px 0"
  },
  metricCard: {
    background: "#f5f7fa",
    borderRadius: 12,
    padding: "10px 8px",
    textAlign: "center"
  },
  metricLabel: {
    fontSize: 11,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4
  },
  metricValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#222"
  }
};
